import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute, Router } from '@angular/router';
import { CountdownComponent } from 'ngx-countdown';
import { ToastrService } from 'ngx-toastr';
import { AngularEditorComponent, AngularEditorConfig } from '@kolkov/angular-editor';
import { applicationMessages, toasterMessages } from '@messages/*';
import { CandidateService } from '../candidate.service';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { LoaderService } from 'src/app/services/loader.service';
import { CodingQuestionViewComponent } from '../coding-question-view/coding-question-view.component';
import { MatDialogRef } from '@angular/material/dialog';
import { questionTypes } from '../assessment-categories/assessment-categories.component';
import { environment } from 'src/environments/environment';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-question-view',
  templateUrl: './question-view.component.html',
  styleUrls: ['./question-view.component.scss']
})

export class QuestionViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cd', { static: false }) private countdown: CountdownComponent;

  // Constants for better maintainability
  private readonly ENTERPRISE_USER_FLAG = 'true';
  private readonly SESSION_STORAGE_KEY = 'mockAssesdata';
  private readonly AUTO_SAVE_INTERVAL_MS = environment.candidateAutosaveMs;
  private readonly DEPRECATED_CODING_TYPE = 'CODING_1'; // TODO: Remove when no longer needed

  submitted = false;
  questions: any = [];
  currentQuestion: any;
  selectedOption: any;
  questionView = true;
  assessmentId: any;
  assessmentData: any;
  questionNumbers: any;
  totalQuestions: any;
  answeredQuestions: any;
  answer: any;
  questionType: string;
  codeLang: number;
  isOpened: any = false;
  isEnterpriseUser: any = false;
  currentModuleType: 'MULTIPLE_CHOICE' | 'OPEN_ENDED' | 'CODING';
  // Expose labels for template usage
  questionTypes = questionTypes;
  String = String; // For template access

  get assessmentInfo() {
    return {
      candidateName: this.assessmentData?.candidateName,
      email: this.assessmentData?.email,
      name: this.assessmentData?.assessmentname,
      duration: this.assessmentData?.time,
      progress: this.assessmentData?.questionsAnsweredpercentage
    };
  }
  // timeSyncInterval : any;
  localData = {
    'MULTIPLE_CHOICE': {
      totalquestions: 0,
      nonAnsweredQuestions: [],
      skippedQuestions: [],
      viewedtill: [],
      questionsAnsweredpercentage: 0,
      answeredQuestions: [],
      questions: {}
    },
    'OPEN_ENDED': {
      totalquestions: 0,
      nonAnsweredQuestions: [],
      skippedQuestions: [],
      viewedtill: [],
      questionsAnsweredpercentage: 0,
      answeredQuestions: [],
      questions: {}
    },
    'CODING': {
      totalquestions: 0,
      nonAnsweredQuestions: [],
      skippedQuestions: [],
      viewedtill: [],
      questionsAnsweredpercentage: 0,
      answeredQuestions: [],
      questions: {}
    }
  };
  questionTypeDuration: number;
  totalModules: number = 0;
  private mutationObserver?: MutationObserver;
  private oeqChanges$ = new Subject<string>();
  private oeqAutosaveSub?: Subscription;
  private autosaveIntervalHandle?: any;
  private isOeqAutoSaveInProgress: boolean = false;
  private lastSavedOeqAnswer?: string;

  config: AngularEditorConfig = {
    height: 'auto',
    minHeight: '0',
    width: 'auto',
    minWidth: '0',
    showToolbar: false
  };

  openEndedConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '400px',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter answer here...',
    defaultParagraphSeparator: 'p',
    defaultFontName: '',
    defaultFontSize: '',
    sanitize: true,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'heading',
        'fontName'
      ],
      [
        'customClasses',
        'unlink',
        'insertVideo',
        'removeFormat'
      ]
    ],
    customClasses: [
      {
        name: 'resizable-image',
        class: 'resizable-image',
        tag: 'img'
      }
    ]
  };

  constructor(
    private toastr: ToastrService,
    private loaderService: LoaderService,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private candidateService: CandidateService) {
    this.questionNumbers = [];
    this.route.queryParams.subscribe(params => {
      this.assessmentId = params?.id;
      this.questionType = params?.type;
      this.isEnterpriseUser = params?.isEnterpriseUser;
      this.questionTypeDuration = params?.questionTypeDuration;
      this.totalModules = Number(params?.totalModules || 0);
    });
  }

  // Redirect to candidate home when backend reports already-submitted
  private redirectIfAlreadySubmitted(err: any): boolean {
    const msg = (err && (err.error?.errors?.[0]?.error || err.error?.error || err.message)) || '';
    if (typeof msg === 'string' && (
      msg.toLowerCase().includes('already has been submitted') ||
      msg.toLowerCase().includes('already answered') ||
      msg.toLowerCase().includes('assessment already submitted') ||
      msg.toLowerCase().includes('Your assessment already has been submitted.')
    )) {
      this.toastr.info('You have already submitted this section or assessment.');
      this.router.navigate(['candidate/assessment-categories'], { queryParams: { id: this.assessmentId } });
      return true;
    }
    return false;
  }

  ngOnInit() {
    if (this.assessmentId && this.questionType && this.isEnterpriseUser === this.ENTERPRISE_USER_FLAG) {
      this.questionNumbers = [];
      this.beginAssessmentForEnterpriseUser(1);
    } else if (this.assessmentId && this.questionType) {
      this.beginAssessment();
    }

    // Debounced autosave pipeline for OPEN_ENDED answers
    if (environment.enableOeqDebouncedAutosave) {
      this.oeqAutosaveSub = this.oeqChanges$.pipe(
        debounceTime(2000),
        distinctUntilChanged(),
        filter(() => this.isOpenEnded() && this.isCurrentAnswerDirty() && !this.isEnterpriseMode())
      ).subscribe(() => {
        this.savePartialOeq().subscribe();
      });
    }

    // Periodic safety save (match coding: 20s)
    this.autosaveIntervalHandle = setInterval(() => {
      if (this.isOpenEnded() && this.isCurrentAnswerDirty() && !this.isEnterpriseMode()) {
        this.savePartialOeq().subscribe();
      }
    }, this.AUTO_SAVE_INTERVAL_MS);
  }


  private beginAssessmentForEnterpriseUser(questionNumber): void {
    this.selectedOption = null;
    this.candidateService.getAssessmentQuestionForEnterprise(this.questionType, this.assessmentId, questionNumber)
      .subscribe((response) => {
        if (sessionStorage.getItem(this.SESSION_STORAGE_KEY)) {
          let data = JSON.parse(sessionStorage.getItem(this.SESSION_STORAGE_KEY));
          response.questionsAnsweredpercentage = data[this.questionType].questionsAnsweredpercentage;
          if (data[this.questionType].questions[questionNumber]) {
            response.question.answer = data[this.questionType].questions[questionNumber].ans;
          }
          response.viewedtill = data[this.questionType].viewedtill
          response.answeredQuestions = data[this.questionType].answeredQuestions;
          response.skippedQuestions = data[this.questionType].skippedQuestions;
        }
        // Use backend-provided remainingModuleTime if available, otherwise calculate from duration
        if (response.remainingModuleTime === undefined || response.remainingModuleTime === null) {
          response.remainingModuleTime = this.questionTypeDuration * 60;
        }
        this.handleQuestionRes(response);
      }, (err) => {
        if (!this.redirectIfAlreadySubmitted(err)) {
          const msg = err?.error?.errors?.[0]?.error || err?.error?.error || err?.message;
          if (msg === 'already answered') {
            this.toastr.error(msg, 'Error');
          } else {
            this.router.navigate(['candidate/expiredAssessment']);
            this.toastr.error(msg, 'Error');
          }
        }
      });
  }

  private beginAssessment() {
    this.questionNumbers = [];
    this.selectedOption = null;
    this.candidateService.beginAssessmentOrViewQuestion({ questionType: this.questionType, view: this.isOpened ? 'full' : 'partial' }, this.assessmentId).subscribe((response) => {
      this.handleQuestionRes(response);
    }, (err) => {
      if (!this.redirectIfAlreadySubmitted(err)) {
        const msg = err?.error?.errors?.[0]?.error || err?.error?.error || err?.message;
        if (msg === 'already answered') {
          this.toastr.error(msg, 'Error');
        } else {
          this.router.navigate(['candidate/expiredAssessment']);
          this.toastr.error(msg, 'Error');
        }
      }
    });
  }

  shuffleArray(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
      const randomIndex = Math.floor(Math.random() * (i + 1));
      [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
    }
    return array;
  }

  private handleQuestionRes(response): void {
    // clearInterval(this.timeSyncInterval);
    // if (
    //   this.isEnterpriseUser !== 'true' && 
    //   response?.question?.id && 
    //   response.question.questionType !== 'CODING'
    // ) { 
    //   this.timeSyncInterval = setInterval(()=>this.timeSync(response?.question?.id), 10000);
    // }
    this.answer = response.question.answer ? response.question.answer : null;
    this.assessmentData = response;
    // Track baseline saved value for OEQ dirty detection
    this.lastSavedOeqAnswer = this.assessmentData?.question?.answer ?? null;
    if (response?.question?.questionType) this.currentModuleType = response?.question?.questionType;

    // If coding module is already completed (all questions answered), auto-submit module
    if (this.questionType === 'CODING' && !this.isEnterpriseMode() && !this.submitted) {
      const isCompletedFlag = response?.isModuleCompleted === true;
      const noneLeft = Array.isArray(response?.nonAnsweredQuestions) && response.nonAnsweredQuestions.length === 0 && (response?.totalquestions ?? 0) > 0;
      if (isCompletedFlag || noneLeft) {
        this.submitted = true; // Prevent duplicate submissions
        this.candidateService.submitAssessment('CODING', this.assessmentId)
          .subscribe(
            (res: any) => {
              if (res?.canDoFinalSubmit) {
                this.candidateService.submitAssessment('', this.assessmentId)
                  .subscribe(
                    () => {
                      this.toastr.success(toasterMessages.submitAssessmentSuccess);
                      this.router.navigate(['candidate/submitted']);
                    },
                    (err) => {
                      this.submitted = false; // Reset on error to allow retry
                      this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to submit overall assessment', 'Error');
                      this.router.navigate(['candidate/assessment-categories'], { queryParams: { id: this.assessmentId } });
                    }
                  );
              } else {
                this.toastr.success(toasterMessages.submitAssessmentModuleSuccess.replace('{module}', 'Coding'));
                this.router.navigate(['candidate/assessment-categories'], { queryParams: { id: this.assessmentId } });
              }
            },
            (err) => {
              this.submitted = false; // Reset on error to allow retry
              this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to submit coding module', 'Error');
            }
          );
      }
    }

    // Only restart timer if module time has changed or timer hasn't started
    // This prevents resetting timer unnecessarily when navigating between questions
    if (this.assessmentData?.remainingModuleTime !== response?.remainingModuleTime) {
      this.countdown?.restart();
    }
    this.selectedOption = response.question.answer ? response.question.answer : null;
    this.questionNumbers = [];
    for (let index = 1; index <= response.totalquestions; index++) {
      const obj = {
        answered: false,
        questionNumber: null,
        viewed: false,
        skipped: false,
        notAnswered: false
      };
      obj.questionNumber = index;
      obj.notAnswered = (response.nonAnsweredQuestions.indexOf(index) !== -1) ? true : false;
      obj.viewed = (response.viewedtill.indexOf(index) !== -1) ? true : false;
      obj.skipped = (response.skippedQuestions?.indexOf(index) !== -1) ? true : false;
      this.questionNumbers.push(obj);
    }

    // Note: Backend already handles MCQ option shuffling in getQuestionObject function
    // No need to shuffle again in frontend to avoid double shuffling issues

    this.totalQuestions = this.assessmentData.totalquestions;
    this.answeredQuestions = this.assessmentData.answeredQuestions?.length;
    if (JSON.stringify(this.assessmentData.question) === '{}') {
      this.questionView = false;
    } else {
      this.questionView = true;
      if (this.assessmentData.question.questionText) {
        this.assessmentData.question.questionText = this.formatQuestionText(this.assessmentData.question.questionText);
      }
    }
  }

  selectAnswerOption(event: MatRadioChange) {
    this.answer = event.value;
  }

  private saveEnterpriseUserProgress(assessmentData: any): void {
    const ans = this.answer || assessmentData?.question?.answer || null;

    if (assessmentData?.currentQuestion > this.totalQuestions) {
      return;
    }

    const currentQuestionIndex = assessmentData.currentQuestion;

    // Load existing data with error handling
    try {
      const existingData = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (existingData) {
        this.localData = JSON.parse(existingData);
      }
    } catch (error) {
      console.warn('Failed to parse mock assessment data from sessionStorage:', error);
      // Reset to default structure if corrupted
      this.localData = this.getDefaultLocalData();
    }

    // Skip deprecated CODING_1 type (TODO: Remove this condition as noted)
    if (this.questionType === this.DEPRECATED_CODING_TYPE) {
      return;
    }

    // Ensure the question type exists in localData
    if (!this.localData[this.questionType]) {
      this.localData[this.questionType] = {
        totalquestions: 0,
        nonAnsweredQuestions: [],
        skippedQuestions: [],
        viewedtill: [],
        questionsAnsweredpercentage: 0,
        answeredQuestions: [],
        questions: {}
      };
    }

    // Update question data
    this.localData[this.questionType].questions[currentQuestionIndex] = { ans };

    // Update answered questions tracking
    const answeredQuestions = this.localData[this.questionType].answeredQuestions;
    const isQuestionExist = answeredQuestions.indexOf(currentQuestionIndex);

    if (!ans && isQuestionExist >= 0) {
      answeredQuestions.splice(isQuestionExist, 1);
    } else if (ans && isQuestionExist === -1) {
      answeredQuestions.push(currentQuestionIndex);
    }

    // Update progress percentage
    this.updatePercentage(answeredQuestions.length, assessmentData.totalquestions);

    // Track viewed questions
    const viewedQuestions = this.localData[this.questionType].viewedtill;
    if (viewedQuestions.indexOf(currentQuestionIndex) === -1) {
      viewedQuestions.push(currentQuestionIndex);
    }

    // Update tracking arrays
    this.updateNonAnsweredQuestions();
    this.updateSkippedQuestions();

    // Cleanup UI
    assessmentData?.componentInstance?.dialogRef?.close();
    this.loaderService.hide();

    // Save to sessionStorage with error handling
    try {
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(this.localData));
    } catch (error) {
      console.error('Failed to save mock assessment data to sessionStorage:', error);
    }
  }

  private getDefaultLocalData() {
    return {
      'MULTIPLE_CHOICE': {
        totalquestions: 0,
        nonAnsweredQuestions: [],
        skippedQuestions: [],
        viewedtill: [],
        questionsAnsweredpercentage: 0,
        answeredQuestions: [],
        questions: {}
      },
      'OPEN_ENDED': {
        totalquestions: 0,
        nonAnsweredQuestions: [],
        skippedQuestions: [],
        viewedtill: [],
        questionsAnsweredpercentage: 0,
        answeredQuestions: [],
        questions: {}
      },
      'CODING': {
        totalquestions: 0,
        nonAnsweredQuestions: [],
        skippedQuestions: [],
        viewedtill: [],
        questionsAnsweredpercentage: 0,
        answeredQuestions: [],
        questions: {}
      }
    };
  }

  saveAndFetchNextQuestion(assessmentData) {
    if (this.isEnterpriseUser === this.ENTERPRISE_USER_FLAG) {
      this.saveEnterpriseUserProgress(assessmentData);
      this.beginAssessmentForEnterpriseUser(assessmentData?.currentQuestion + 1);
    } else {
      this.questionView = true;
      // For MCQ, prioritize selectedOption as it's the actual bound value
      // For OPEN_ENDED, prefer the bound editor value stored in assessmentData.question.answer
      let ans = null;
      if (assessmentData?.question?.questionType === 'MULTIPLE_CHOICE') {
        ans = this.selectedOption ?? this.answer ?? assessmentData?.question?.answer ?? null;
      } else {
        ans = (assessmentData?.question?.answer ?? this.answer ?? null);
      }
      const codeLang = this.codeLang ? this.codeLang : '';
      const reqObj = { question: { question: assessmentData?.question?.id, answer: ans, questionSignature_id: codeLang }, isOpened: this.isOpened };
      this.candidateService.getNextQuestion(this.questionType, reqObj, this.assessmentId).subscribe(
        (response: any) => {
          // Handle both regular candidate responses and preview responses
          const isPreview = !!response?.preview;
          if (!isPreview) {
            this.handleQuestionRes(response);
          }

          // If this is CODING module and no remaining questions, auto-submit the module
          const isCoding = this.questionType === 'CODING' && !this.isEnterpriseMode();
          const noRemainingCoding =
            isCoding &&
            !this.submitted && // Prevent duplicate submissions
            Array.isArray(response?.nonAnsweredQuestions) &&
            response.nonAnsweredQuestions.length === 0 &&
            (response?.totalquestions ?? 0) > 0; // Ensure there were questions to begin with

          if (noRemainingCoding) {
            this.submitted = true; // Set flag to prevent duplicate submissions
            this.candidateService.submitAssessment('CODING', this.assessmentId)
              .subscribe(
                (res: any) => {
                  // If all modules done, auto-submit overall
                  if (res?.canDoFinalSubmit) {
                    this.candidateService.submitAssessment('', this.assessmentId)
                      .subscribe(
                        (_final: any) => {
                          this.loaderService.hide();
                          // Close coding dialog with result so caller can navigate
                          assessmentData?.componentInstance?.dialogRef?.close({ moduleSubmitted: true, finalSubmitted: true, res: _final });
                        },
                        (err) => {
                          this.loaderService.hide();
                          this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to submit overall assessment', 'Error');
                          assessmentData?.componentInstance?.dialogRef?.close({ moduleSubmitted: true, finalSubmitted: false, error: err });
                        }
                      );
                  } else {
                    this.loaderService.hide();
                    assessmentData?.componentInstance?.dialogRef?.close({ moduleSubmitted: true, res });
                  }
                },
                (err) => {
                  this.submitted = false; // Reset flag on error to allow retry
                  this.loaderService.hide();
                  this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to submit coding module', 'Error');
                  assessmentData?.componentInstance?.dialogRef?.close({ moduleSubmitted: false, error: err });
                }
              );
          } else {
            this.isOpened = false;
            this.loaderService.hide();
            assessmentData?.componentInstance?.dialogRef?.close();
          }
        },
        (err) => {
          this.loaderService.hide();
          if (!this.redirectIfAlreadySubmitted(err)) {
            // Improved error handling with better user feedback
            const errorMessage = err?.error?.errors?.[0]?.error ||
              err?.error?.error ||
              err?.message ||
              'Failed to save answer. Please try again.';
            this.toastr.error(errorMessage, 'Error');
            console.error('Save question error:', err);
          }
        }
      );
    }
  }

  nextQuestionHandler() {
    // Always persist current question state before navigating so unanswered ones are marked as VIEWED (skipped)
    this.saveAndFetchNextQuestion(this.assessmentData);
  }

  submitAnswers(nextQuestion = null, autoSubmit: boolean = false) {
    if (!autoSubmit) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'delete-dialog-container',
        data: { danger: false, messageBody: applicationMessages.submitAssessmentConfirm }
      });

      dialogRef?.afterClosed()?.subscribe(result => {
        if (result === 'confirm') {
          this.assessmentConfirmSubmit(nextQuestion);
        }
      });
    } else {
      this.assessmentConfirmSubmit(nextQuestion);
    }
  }

  private assessmentConfirmSubmit(nextQuestion) {
    if (this.isEnterpriseUser === this.ENTERPRISE_USER_FLAG) {
      this.router.navigate(['candidate/assessment-categories'],
        { queryParams: { id: this.assessmentId, isEnterpriseUser: this.isEnterpriseUser, questionType: this.questionType } });
    } else if (nextQuestion && nextQuestion?.question?.id) {
      // For MCQ, prioritize selectedOption as it's the actual bound value
      let ans = null;
      if (nextQuestion?.question?.questionType === 'MULTIPLE_CHOICE') {
        ans = this.selectedOption ?? this.answer ?? nextQuestion?.question?.answer ?? null;
      } else {
        ans = (nextQuestion?.question?.answer ?? this.answer ?? null);
      }
      const codeLang = this.codeLang ? this.codeLang : '';
      const reqObj = { question: { question: nextQuestion?.question?.id, answer: ans, questionSignature_id: codeLang }, isOpened: this.isOpened };
      this.candidateService.getNextQuestion(this.questionType, reqObj, this.assessmentId).subscribe((response: any) => {
        this.handleQuestionRes(response);
        this.isOpened = false;
        this.loaderService.hide();
        nextQuestion?.componentInstance?.dialogRef?.close();
        this.SubmitAssessmentApiCall();
      }, (err) => {
        this.loaderService.hide();
        if (!this.redirectIfAlreadySubmitted(err)) {
          this.toastr.error(err?.error?.errors[0]?.error, 'Error');
        }
      });
    } else {
      this.SubmitAssessmentApiCall();
    }
  }


  SubmitAssessmentApiCall(): void {
    this.candidateService.submitAssessment(this.questionType, this.assessmentId)
      .subscribe((res: any) => {
        // clearInterval(this.timeSyncInterval);
        this.submitted = true;
        if (res?.canDoFinalSubmit) {
          this.submitAssessment();
        } else {
          this.toastr.success(toasterMessages.submitAssessmentModuleSuccess.replace('{module}', questionTypes[this.questionType]));
          this.router.navigate(['candidate/assessment-categories'],
            { queryParams: { id: this.assessmentId } });
        }
      }, (err) => {
        if (!this.redirectIfAlreadySubmitted(err)) {
          this.toastr.error(err?.error?.errors[0]?.error, 'Error');
        }
      });
  }

  private submitAssessment(): void {
    this.candidateService.submitAssessment('', this.assessmentId)
      .subscribe((res: any) => {
        this.toastr.success(toasterMessages.submitAssessmentSuccess);
        this.router.navigate(['candidate/submitted']);
      }, (err) => {
        this.toastr.error(err?.error?.errors[0]?.error, 'Error');
      });
  }

  saveAnswers() {
    //TODO: need to do api call with current question.
    this.toastr.success(toasterMessages.candidateSaveAnswer);
  }

  // Helper to check if the current answer is dirty (unsaved)
  private isCurrentAnswerDirty(): boolean {
    const isMCQ = this.assessmentData?.question?.questionType === 'MULTIPLE_CHOICE';
    const isOpenEnded = this.assessmentData?.question?.questionType === 'OPEN_ENDED';
    let dirty = false;
    if (isMCQ) {
      // For MCQ, compare the bound value against the last saved answer from backend
      const currentAnswer = this.selectedOption ?? this.answer;
      dirty = currentAnswer !== this.assessmentData?.question?.answer;
      return dirty;
    }
    if (isOpenEnded) {
      const current = this.assessmentData?.question?.answer ?? '';
      const baseline = this.lastSavedOeqAnswer ?? '';
      dirty = current !== baseline;
      return dirty;
    }
    // For Coding: implement as needed
    return false;
  }

  questionNumberClick(num: any) {
    // Avoid unnecessary API calls if already on this question
    if (this.assessmentData?.currentQuestion === num) {
      return;
    }
    // Save current answer if dirty before navigating
    if (this.isCurrentAnswerDirty()) {
      // For MCQ, prioritize selectedOption as it's the actual bound value
      let ans = null;
      if (this.assessmentData?.question?.questionType === 'MULTIPLE_CHOICE') {
        ans = this.selectedOption ?? this.answer ?? this.assessmentData?.question?.answer ?? null;
      } else {
        ans = (this.assessmentData?.question?.answer ?? this.answer ?? null);
      }
      const codeLang = this.codeLang ? this.codeLang : '';
      const reqObj = { question: { question: this.assessmentData?.question?.id, answer: ans, questionSignature_id: codeLang }, isOpened: this.isOpened };
      this.candidateService.getNextQuestion(this.questionType, reqObj, this.assessmentId).subscribe(
        () => {
          this.getQuestion(num);
        },
        (err) => {
          this.toastr.error(err?.error?.errors[0]?.error, 'Error');
        }
      );
    } else {
      this.getQuestion(num);
    }
  }

  previousQuestion(currentQuestion) {
    const num = currentQuestion?.currentQuestion - 1;
    this.getQuestion(num);
  }

  private getQuestion(num: number) {
    if (this.isEnterpriseUser === this.ENTERPRISE_USER_FLAG) {
      this.beginAssessmentForEnterpriseUser(num);
    } else {
      this.candidateService.getQuestionByIndex(this.assessmentId, {
        questionType: this.questionType,
        questionNumber: num,
        view: this.questionType === 'CODING' && this.isOpened ? 'full' : 'partial'
      }).subscribe((response: any) => {
        this.questionView = true;
        this.handleQuestionRes(response);
      }, (err) => {
        if (!this.redirectIfAlreadySubmitted(err)) {
          this.toastr.error(err?.error?.errors[0]?.error, 'Error');
        }
      });
    }
  }

  getShortName(fullName) {
    return fullName?.split(' ').map(n => n[0]).join('');
  }

  handleEvent(e) {
    if (e.action === 'notify') {
      this.toastr.warning(toasterMessages.candidateAssessmentTimeWarning.replace('{timeLeft}', `${Math.floor(e.left / 60000)}`));
    }
    if (e.action === 'done') {
      this.toastr.info(toasterMessages.candidateAssessmentInfo);
      // On timer auto-submit, ensure current answer is saved first if dirty
      if (this.isOpenEnded() && this.isCurrentAnswerDirty() && !this.isEnterpriseMode()) {
        this.savePartialOeq().subscribe({
          next: () => this.submitAnswers(null, true),
          error: () => this.submitAnswers(null, true)
        });
        // For MCQ, save only the current question on auto-submit, then submit
      } else if (this.isMultipleChoice() && !this.isEnterpriseMode()) {
        this.submitAnswers(this.assessmentData, true);
      } else if (this.currentModuleType === 'CODING' && !this.isEnterpriseMode() && !this.submitted) {
        // If the coding editor dialog is open, let it handle the auto-submission (it has its own timer handler)
        if (this.isOpened) {
          return;
        }
        // For coding questions, save current answer before auto-submitting
        if (this.assessmentData?.question?.id) {
          const ans = this.assessmentData?.question?.answer || '';
          const codeLang = this.codeLang || '';
          const reqObj = {
            question: {
              question: this.assessmentData.question.id,
              answer: ans,
              questionSignature_id: codeLang
            },
            partialSave: true
          };
          this.candidateService.getNextQuestion(this.questionType, reqObj, this.assessmentId).subscribe({
            next: () => this.submitAnswers(null, true),
            error: () => this.submitAnswers(null, true) // Submit even if save fails
          });
        } else {
          this.submitAnswers(null, true);
        }
      } else {
        this.submitAnswers(null, true);
      }
    }
    if (e.action === 'restart') {
      // Handle restart action
    }
  }

  openCodingEditor() {
    if (this.isEnterpriseUser === this.ENTERPRISE_USER_FLAG) {
      const codingDialogRef = this.dialog.open(CodingQuestionViewComponent, {
        data: {
          danger: false,
          details: this.assessmentData,
          assessmentid: this.assessmentId,
          isEnterpriseUser: this.isEnterpriseUser
        },
        disableClose: true,
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%'
      });

      this.isOpened = true;
      codingDialogRef.componentInstance.nextQuestion.subscribe((question: any) => {
        this.loaderService.show('Loading next question');
        this.answer = question.source_code;
        this.questionType = 'CODING';
        this.codeLang = question.language_id;
        this.saveAndFetchNextQuestion({ ...this.assessmentData, ...codingDialogRef });
      });

      codingDialogRef.afterClosed().subscribe(result => {
        if (result?.moduleSubmitted) {
          if (result?.finalSubmitted) {
            // Overall assessment was auto-submitted, navigate to submitted page
            this.router.navigate(['candidate/submitted']);
          } else {
            // Only coding module was submitted, return to categories
            this.toastr.success(toasterMessages.submitAssessmentModuleSuccess.replace('{module}', 'Coding'));
            this.router.navigate(['candidate/assessment-categories'],
              { queryParams: { id: this.assessmentId } });
          }
        }
      });

    } else {
      let codingDialogRef: MatDialogRef<CodingQuestionViewComponent, any>;
      this.candidateService.beginAssessmentOrViewQuestion({
        questionType: this.questionType,
        questionNumber: this.assessmentData.currentQuestion,
        view: 'full'
      }, this.assessmentId).subscribe((response) => {
        this.handleQuestionRes(response);
        codingDialogRef = this.dialog.open(CodingQuestionViewComponent, {
          data: {
            danger: false,
            details: this.assessmentData,
            assessmentid: this.assessmentId,
            isEnterpriseUser: this.isEnterpriseUser
          },
          disableClose: true,
          maxWidth: '100vw',
          maxHeight: '100vh',
          height: '100%',
          width: '100%'
        });

        this.isOpened = true;
        codingDialogRef.componentInstance.nextQuestion.subscribe((question: any) => {
          this.loaderService.show('Loading next question');
          this.answer = question.source_code;
          this.questionType = 'CODING';
          this.codeLang = question.language_id;
          // clearInterval(this.timeSyncInterval);
          this.saveAndFetchNextQuestion({ ...this.assessmentData, ...codingDialogRef });
        });

        codingDialogRef.afterClosed().subscribe(result => {
          // clearInterval(this.timeSyncInterval);
          if (result?.moduleSubmitted) {
            if (result?.finalSubmitted) {
              // Overall assessment was auto-submitted, navigate to submitted page
              this.router.navigate(['candidate/submitted']);
            } else {
              // Only coding module was submitted, return to categories
              this.toastr.success(toasterMessages.submitAssessmentModuleSuccess.replace('{module}', 'Coding'));
              this.router.navigate(['candidate/assessment-categories'],
                { queryParams: { id: this.assessmentId } });
            }
          }
        });
      }, (err) => {
        const msg = err?.error?.errors?.[0]?.error || err?.error?.error || err?.message;
        if (msg === 'already answered') {
          this.toastr.error(msg, 'Error');
        } else {
          this.router.navigate(['candidate/expiredAssessment']);
          this.toastr.error(msg, 'Error');
        }
      });
    }

    // if (
    //   this.isEnterpriseUser !== 'true' &&
    //   this.assessmentData.question.id
    // ) {
    //   this.timeSyncInterval = setInterval(() => this.timeSync(this.assessmentData.question.id), 10000);
    // }
  }

  updatePercentage(answered, toQuestions) {
    const pDivider = answered / toQuestions;
    this.localData[this.questionType].questionsAnsweredpercentage = (pDivider * 100).toFixed(2);
  }

  updateNonAnsweredQuestions() {
    this.localData[this.questionType].nonAnsweredQuestions = [];
    for (var i = 1; i <= this.totalQuestions; i++) {

      if (this.localData[this.questionType].answeredQuestions.indexOf(i) === -1) {
        this.localData[this.questionType].nonAnsweredQuestions.push(i);
      }
    }
  };

  updateSkippedQuestions() {
    this.localData[this.questionType].skippedQuestions = [];
    const viewedTillArray = this.localData[this.questionType].viewedtill;
    const nonAnsQues = this.localData[this.questionType].nonAnsweredQuestions;
    for (var i = 1; i <= viewedTillArray.length; i++) {
      if (nonAnsQues.indexOf(viewedTillArray[i - 1]) !== -1) {
        this.localData[this.questionType].skippedQuestions.push(nonAnsQues[nonAnsQues.indexOf(viewedTillArray[i - 1])]);
      }
    }
  };


  // No MutationObserver or direct DOM overrides; rely on stable binding and CSS
  ngAfterViewInit() { }

  ngOnDestroy() {
    if (this.autosaveIntervalHandle) {
      clearInterval(this.autosaveIntervalHandle);
    }
    if (this.oeqAutosaveSub) {
      this.oeqAutosaveSub.unsubscribe();
    }
  }

  // Open-ended helpers and autosave
  private isOpenEnded(): boolean {
    return this.assessmentData?.question?.questionType === 'OPEN_ENDED';
  }

  private isEnterpriseMode(): boolean {
    return this.isEnterpriseUser === this.ENTERPRISE_USER_FLAG;
  }

  private isMultipleChoice(): boolean {
    return this.assessmentData?.question?.questionType === 'MULTIPLE_CHOICE';
  }

  onOeqChanged(_: string) {
    // Push the new value to the debounced stream
    this.oeqChanges$.next(this.assessmentData?.question?.answer || '');
  }

  private savePartialOeq() {
    if (!this.isOpenEnded() || !this.assessmentData?.question?.id) {
      return of(null);
    }
    if (this.isOeqAutoSaveInProgress) {
      return of(null);
    }
    this.isOeqAutoSaveInProgress = true;
    const ans = this.assessmentData?.question?.answer ?? this.answer ?? null;
    // Skip blank answers
    if (!ans || String(ans).trim() === '') {
      this.isOeqAutoSaveInProgress = false;
      return of(null);
    }
    // Avoid spamming backend with same payload
    if (this.lastSavedOeqAnswer === ans) {
      this.isOeqAutoSaveInProgress = false;
      return of(null);
    }
    // Enterprise flow stores locally only; skip backend call
    if (this.isEnterpriseMode()) {
      this.isOeqAutoSaveInProgress = false;
      return of(null);
    }
    const reqObj = {
      question: {
        question: this.assessmentData?.question?.id,
        answer: ans,
        // omit questionSignature_id for open ended
      },
      isOpened: this.isOpened,
      partialSave: true
    };
    return this.candidateService.getNextQuestion(this.questionType, reqObj, this.assessmentId).pipe(
      tap(() => {
        this.lastSavedOeqAnswer = ans;
      }),
      catchError((err) => {
        // Silent fail for autosave to avoid UX interruption
        return of(null);
      }),
      tap(() => {
        this.isOeqAutoSaveInProgress = false;
      })
    );
  }

  formatQuestionText(text: string): string {
    if (!text) return '';
    let formatted = text;

    // Handle escaped newlines (common in JSON data)
    formatted = formatted.replace(/\\n/g, '\n');

    // 1. Strip triple backtick wrappers if they exist
    formatted = formatted.replace(/^```[a-z]*\n([\s\S]*?)\n```$/g, '$1');
    formatted = formatted.replace(/^```[\s\S]*?```$/g, (match) => match.replace(/```/g, ''));

    // 2. Format Headers (# or ##) -> Bold + Newline
    formatted = formatted.replace(/^#+\s*(.*)$/gm, '<b>$1</b>');

    // 3. Handle Markdown bold: **text** -> <b>text</b>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

    // 4. Handle Inline Code: `code` -> <code>code</code>
    formatted = formatted.replace(/`(.*?)`/g, '<code style="background: #f1f5f9; padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 0.9em; color: #e11d48;">$1</code>');

    // 5. Handle Bullet Points: "- " or "* " at start of line -> Bullet
    formatted = formatted.replace(/^\s*[\-\*]\s+/gm, '&bull; ');

    // 6. Handle Newlines: \n -> <br>
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  }
}
